"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@clerk/nextjs";
import { Activity, ArrowRight, BarChart3, CheckCircle2, FileSpreadsheet, FolderKanban, Gavel, Handshake, Leaf, MapPin, Newspaper, Shield, Sprout, TrendingUp, Users } from "lucide-react";
import Link from "next/link";
import { PageEyebrow } from "@/components/public/page-eyebrow";
import { PUBLIC_CONTAINER, PUBLIC_GRID_GAP, PUBLIC_PANEL_RADIUS, PUBLIC_SECTION_PY_MARKETING } from "@/components/public/public-layout-styles";
import { Badge } from "@/components/ui/badge";
import { ButtonLink } from "@/components/ui/button";
import { authEntryHref, authEntryLabel } from "@/lib/auth/sign-up";
import { cn } from "@/lib/utils";
export function LandingContent() {
  const $ = _c(36);
  const {
    isSignedIn
  } = useAuth();
  let t0;
  if ($[0] !== isSignedIn) {
    t0 = <HeroProductPreview isSignedIn={isSignedIn} />;
    $[0] = isSignedIn;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(PUBLIC_CONTAINER, "py-12 sm:py-16");
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <section aria-labelledby="how-it-works-heading" className="border-border/50 bg-background/70 relative z-10 border-y" id="como-funciona"><div className={t1}><div className="mb-8"><h2 className="max-w-[40ch] text-2xl font-semibold tracking-tight text-balance sm:text-3xl" id="how-it-works-heading">Como funciona</h2><p className="text-muted-foreground mt-3 max-w-[48ch] text-lg text-pretty">Feito para consultorias, cooperativas e equipes que precisam transformar dados de campo em projetos financiáveis.</p></div><ol className={cn("border-border/50 divide-border/50 divide-y border sm:grid sm:grid-cols-3 sm:divide-x sm:divide-y-0", PUBLIC_PANEL_RADIUS)}><WorkflowStep description={"Produtores, propriedades e documentos em um fluxo \xFAnico de cadastro."} step={1} title="Cadastrar" /><WorkflowStep description={"Or\xE7amento, etapas t\xE9cnicas e anexos prontos para an\xE1lise de cr\xE9dito rural."} step={2} title="Projetar" /><WorkflowStep description={"Relat\xF3rios padronizados para apresentar ao financiamento com consist\xEAncia."} step={3} title={"Relat\xF3rio"} /></ol></div></section>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(PUBLIC_CONTAINER, "py-16 sm:py-24");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mb-16"><h2 className="max-w-[35ch] text-3xl font-semibold tracking-tight text-balance sm:text-4xl">Tudo que você precisa em um só lugar</h2><p className="text-muted-foreground mt-4 max-w-[56ch] text-lg text-pretty">Estruture, acompanhe e apresente projetos agrícolas com clareza operacional, sem planilhas espalhadas.</p></div>;
    t5 = cn("grid md:grid-cols-2 lg:grid-cols-3", PUBLIC_GRID_GAP);
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FeatureCard category="Projetar" className="md:col-span-2 lg:col-span-2" description={"Estruture projetos de custeio e investimento com dados t\xE9cnicos, or\xE7amento e documenta\xE7\xE3o pronta para an\xE1lise, do cadastro do produtor ao pacote final para o banco."} featured={true} icon={<FolderKanban className="size-6" />} title="Projetos de Custeio e Investimento" />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FeatureCard category="Cadastrar" description={"Cadastro completo de produtores rurais com informa\xE7\xF5es de propriedades, documentos e hist\xF3rico de opera\xE7\xF5es."} icon={<Users className="size-6" />} title={"Gest\xE3o de Produtores"} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FeatureCard category="Documentar" description={"Gere relat\xF3rios t\xE9cnicos e documentos profissionais para apresentar projetos agr\xEDcolas com padroniza\xE7\xE3o."} icon={<FileSpreadsheet className="size-6" />} title={"Relat\xF3rios Autom\xE1ticos"} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <FeatureCard category="Analisar" description={"Visualize a opera\xE7\xE3o rural em tempo real com m\xE9tricas de projetos, produtores e desempenho financeiro."} icon={<TrendingUp className="size-6" />} title={"Dashboard Anal\xEDtico"} />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FeatureCard category="Controlar" comingSoon={true} description={"Acompanhe receitas, despesas, fluxo de caixa e indicadores financeiros ligados a cada projeto agr\xEDcola."} icon={<BarChart3 className="size-6" />} title="Controle Financeiro" />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <section className="border-border/50 bg-surface relative z-10 border-y"><div className={t3}>{t4}<div className={t5}>{t6}{t7}{t8}{t9}{t10}<FeatureCard category="Relacionar" comingSoon={true} description={"Gerencie relacionamento com produtores, clientes, hist\xF3rico de contatos e oportunidades comerciais."} icon={<Handshake className="size-6" />} title="CRM Integrado" /></div></div></section>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(PUBLIC_CONTAINER, PUBLIC_SECTION_PY_MARKETING);
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mb-8"><h2 className="max-w-[35ch] text-3xl font-semibold tracking-tight text-balance sm:text-4xl" id="content-hub-heading">Acompanhe o setor em tempo real</h2><p className="text-muted-foreground mt-4 max-w-[48ch] text-lg text-pretty">Notícias curadas e atualizações oficiais sobre crédito rural, Plano Safra e política agrícola. Acesso público, sem login.</p></div>;
    t14 = cn("grid md:grid-cols-2", PUBLIC_GRID_GAP);
    $[14] = t13;
    $[15] = t14;
  } else {
    t13 = $[14];
    t14 = $[15];
  }
  let t15;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("border-border/50 bg-card hover:border-brand-accent/30 group flex flex-col gap-3 border p-6 motion-safe:transition-colors", PUBLIC_PANEL_RADIUS);
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  let t17;
  let t18;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Newspaper className="text-brand-accent size-6" />;
    t17 = <h3 className="text-lg font-semibold">Notícias do agronegócio</h3>;
    t18 = <p className="text-muted-foreground text-base text-pretty">Curadoria diária de fontes jornalísticas e institucionais relevantes para projetos financiáveis.</p>;
    $[17] = t16;
    $[18] = t17;
    $[19] = t18;
  } else {
    t16 = $[17];
    t17 = $[18];
    t18 = $[19];
  }
  let t19;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Link className={t15} href="/noticias">{t16}{t17}{t18}<span className="text-brand-accent mt-auto inline-flex items-center gap-1 text-sm font-medium">Ver notícias<ArrowRight className="size-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5" /></span></Link>;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn("border-border/50 bg-card hover:border-brand-accent/30 group flex flex-col gap-3 border p-6 motion-safe:transition-colors", PUBLIC_PANEL_RADIUS);
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  let t21;
  let t22;
  let t23;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Gavel className="text-brand-accent size-6" />;
    t22 = <h3 className="text-lg font-semibold">Legislação Agro e Plano Safra</h3>;
    t23 = <p className="text-muted-foreground text-base text-pretty">Monitor de comunicados oficiais, normas do CMN e referências para crédito rural.</p>;
    $[22] = t21;
    $[23] = t22;
    $[24] = t23;
  } else {
    t21 = $[22];
    t22 = $[23];
    t23 = $[24];
  }
  let t24;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <section aria-labelledby="content-hub-heading" className="border-border/50 bg-background/70 relative z-10 border-y"><div className={t12}>{t13}<div className={t14}>{t19}<Link className={t20} href="/legislacao-agro">{t21}{t22}{t23}<span className="text-brand-accent mt-auto inline-flex items-center gap-1 text-sm font-medium">Ver legislação<ArrowRight className="size-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5" /></span></Link></div></div></section>;
    $[25] = t24;
  } else {
    t24 = $[25];
  }
  let t25;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = cn(PUBLIC_CONTAINER, "py-16 sm:py-24");
    $[26] = t25;
  } else {
    t25 = $[26];
  }
  let t26;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="pointer-events-none absolute inset-0 opacity-10"><div className="absolute inset-0" style={{
        backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
        backgroundSize: "32px 32px"
      }} /></div>;
    $[27] = t26;
  } else {
    t26 = $[27];
  }
  let t27;
  let t28;
  let t29;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Leaf className="mx-auto mb-6 size-12 text-white/90" />;
    t28 = <h2 className="mx-auto mb-4 max-w-[35ch] text-3xl font-semibold text-balance text-white sm:text-4xl">Estruture seu próximo projeto hoje</h2>;
    t29 = <p className="mx-auto mb-8 max-w-[48ch] text-lg text-pretty text-white/90">Centralize produtores, orçamentos e relatórios técnicos antes da análise de financiamento, sem planilhas paralelas.</p>;
    $[28] = t27;
    $[29] = t28;
    $[30] = t29;
  } else {
    t27 = $[28];
    t28 = $[29];
    t29 = $[30];
  }
  let t30;
  if ($[31] !== isSignedIn) {
    t30 = <section className="relative z-10 overflow-hidden"><div className={t25}><div className="from-brand-primary to-brand-accent shadow-brand-accent/20 relative overflow-hidden rounded-2xl bg-linear-to-br p-8 text-center shadow-2xl sm:p-12 lg:p-16">{t26}<div className="relative">{t27}{t28}{t29}<CtaActions isSignedIn={isSignedIn} /></div></div></div></section>;
    $[31] = isSignedIn;
    $[32] = t30;
  } else {
    t30 = $[32];
  }
  let t31;
  if ($[33] !== t0 || $[34] !== t30) {
    t31 = <>{t0}{t2}{t11}{t24}{t30}</>;
    $[33] = t0;
    $[34] = t30;
    $[35] = t31;
  } else {
    t31 = $[35];
  }
  return t31;
}

/* ---------------- Hero: Product preview ---------------- */

type HeroProps = {
  isSignedIn: boolean | undefined;
};
function HeroProductPreview(t0) {
  const $ = _c(14);
  const {
    isSignedIn
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("relative z-10", PUBLIC_CONTAINER, PUBLIC_SECTION_PY_MARKETING);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PageEyebrow className="mb-6" icon={Sprout}>Software de gestão para projetos agrícolas</PageEyebrow>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <h1 className="mb-6 max-w-[24ch] text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">Do cadastro do produtor ao relatório final,{" "}<span className="from-brand-primary to-brand-accent bg-linear-to-r bg-clip-text text-transparent">em um só lugar</span></h1>;
    t4 = <p className="text-muted-foreground mb-8 max-w-[40ch] text-lg text-pretty sm:text-xl">Estruture projetos, consolide orçamentos e gere documentos técnicos padronizados para análise de financiamento rural.</p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isSignedIn) {
    t5 = <HeroActions isSignedIn={isSignedIn} />;
    $[4] = isSignedIn;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center gap-2"><Shield className="text-brand-accent size-5 sm:size-4" /><span>Dados Seguros</span></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center gap-2"><CheckCircle2 className="text-brand-accent size-5 sm:size-4" /><span>Conformidade LGPD</span></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="text-muted-foreground mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-base sm:text-sm">{t6}{t7}<div className="flex items-center gap-2"><TrendingUp className="text-brand-accent size-5 sm:size-4" /><span>Suporte Especializado</span></div></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t5) {
    t9 = <div className="min-w-0">{t2}{t3}{t4}{t5}{t8}</div>;
    $[9] = t5;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="relative mx-auto w-full max-w-2xl min-w-0"><ProductPreviewCard /></div>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t9) {
    t11 = <section className={t1}><div className="grid items-center gap-12 lg:grid-cols-[5fr_6fr]">{t9}{t10}</div></section>;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  return t11;
}
function HeroActions(t0) {
  const $ = _c(3);
  const {
    isSignedIn
  } = t0;
  if (isSignedIn === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div aria-hidden="true" className="h-12" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (isSignedIn) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex flex-col gap-4 sm:flex-row"><ButtonLink size="hero" variant="marketing" href="/app">Acessar Dashboard<ArrowRight className="size-5" /></ButtonLink></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col gap-4 sm:flex-row"><ButtonLink size="hero" variant="marketing" href={authEntryHref}>{authEntryLabel}<ArrowRight className="size-5" /></ButtonLink><ButtonLink size="hero" variant="outline" href="/sign-in">Já tenho conta</ButtonLink></div>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function CtaActions(t0) {
  const $ = _c(3);
  const {
    isSignedIn
  } = t0;
  if (isSignedIn === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div aria-hidden="true" className="h-12" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (isSignedIn) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex flex-wrap items-center justify-center gap-3"><ButtonLink className="border border-white/70 bg-white/35 font-semibold text-white hover:bg-white/45 hover:text-white" size="hero" variant="ghost" href="/app">Acessar Dashboard<ArrowRight className="size-5" /></ButtonLink></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex flex-col items-center justify-center gap-4 sm:flex-row"><ButtonLink className="border border-white/70 bg-white/35 font-semibold text-white hover:bg-white/45 hover:text-white" size="hero" variant="ghost" href={authEntryHref}>{authEntryLabel}<ArrowRight className="size-5" /></ButtonLink><ButtonLink className="border-white/70 bg-transparent text-white hover:bg-white/10 hover:text-white" size="hero" variant="outline" href="/sign-in">Já tenho conta</ButtonLink></div>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function ProductPreviewCard() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="from-brand-primary/30 to-brand-accent/20 absolute -inset-2 rounded-4xl bg-linear-to-br blur-2xl sm:-inset-6" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="border-border/60 bg-surface flex items-center gap-2 border-b px-4 py-3"><span className="bg-negative/70 size-2.5 rounded-full" /><span className="bg-warning/70 size-2.5 rounded-full" /><span className="bg-success/70 size-2.5 rounded-full" /><div className="bg-background/60 text-muted-foreground ml-3 flex-1 rounded-md px-3 py-1 text-xs">agroproj.com / projetos / 2026</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="bg-border/60 grid grid-cols-1 gap-px sm:grid-cols-3"><PreviewStat label="Projetos ativos" sub={"+12 este m\xEAs"} value="128" /><PreviewStat label={"Or\xE7amento total"} sub="12 contratos" value="R$ 42,8M" /><PreviewStat label="Produtores" sub="5 estados" value="1.240" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mb-3 flex items-center justify-between"><p className="text-sm font-semibold">Projetos recentes</p><span className="text-muted-foreground text-xs">Abril 2026</span></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="border-border/60 bg-card relative overflow-hidden rounded-2xl border shadow-2xl">{t1}{t2}<div className="bg-card p-5">{t3}<ul className="divide-border/60 divide-y"><PreviewRow amount="R$ 1,8M" name={"Fazenda Santa Rita \xB7 Soja"} state="PR" status={"Em an\xE1lise"} statusTone="warning" /><PreviewRow amount="R$ 3,2M" name={"Cooperativa Sert\xE3o \xB7 Irriga\xE7\xE3o"} state="BA" status="Aprovado" statusTone="success" /><PreviewRow amount="R$ 960K" name={"S\xEDtio Boa Vista \xB7 Caf\xE9"} state="MG" status={"Em edi\xE7\xE3o"} statusTone="info" /></ul></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="relative">{t0}{t4}<div className="border-border/60 bg-card absolute -bottom-6 -left-6 hidden rounded-xl border p-4 shadow-xl xl:block"><div className="flex items-center gap-3"><span className="from-brand-primary to-brand-accent flex size-10 items-center justify-center rounded-lg bg-linear-to-br text-white"><Activity className="size-5" /></span><div><p className="text-muted-foreground text-xs">Taxa de aprovação</p><p className="text-sm font-semibold tabular-nums">94,2%</p></div></div></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function WorkflowStep(t0) {
  const $ = _c(10);
  const {
    description,
    step,
    title
  } = t0;
  let t1;
  if ($[0] !== step) {
    t1 = <span className="text-brand-accent text-sm font-semibold tabular-nums">Etapa {step}</span>;
    $[0] = step;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h3 className="mt-2 text-lg font-semibold">{title}</h3>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <p className="text-muted-foreground mt-2 text-base text-pretty">{description}</p>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <li className="p-5 sm:px-6">{t1}{t2}{t3}</li>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function PreviewStat(t0) {
  const $ = _c(10);
  const {
    label,
    sub,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-muted-foreground text-xs">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== sub) {
    t3 = <p className="text-brand-accent mt-1 text-xs">{sub}</p>;
    $[4] = sub;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="bg-card p-5">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function PreviewRow(t0) {
  const $ = _c(17);
  const {
    amount,
    name,
    state,
    status,
    statusTone
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = <p className="truncate text-sm font-medium">{name}</p>;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MapPin className="size-3" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== state) {
    t3 = <p className="text-muted-foreground flex items-center gap-1 text-xs">{t2}{state}</p>;
    $[3] = state;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <div className="min-w-0 flex-1">{t1}{t3}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== amount) {
    t5 = <span className="text-sm font-semibold tabular-nums">{amount}</span>;
    $[8] = amount;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== status || $[11] !== statusTone) {
    t6 = <Badge variant={statusTone}>{status}</Badge>;
    $[10] = status;
    $[11] = statusTone;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <li className="flex items-center justify-between gap-4 py-3">{t4}{t5}{t6}</li>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/* ---------------- Feature cards ---------------- */

type FeatureCardProps = {
  category: string;
  className?: string;
  comingSoon?: boolean;
  description: string;
  featured?: boolean;
  icon: React.ReactNode;
  title: string;
};
function FeatureCard(t0) {
  const $ = _c(31);
  const {
    category,
    className,
    comingSoon: t1,
    description,
    featured: t2,
    icon,
    title
  } = t0;
  const comingSoon = t1 === undefined ? false : t1;
  const featured = t2 === undefined ? false : t2;
  const t3 = comingSoon ? "opacity-80" : "hover:border-brand-accent/30 hover:shadow-brand-accent/5 motion-safe:hover:shadow-lg";
  const t4 = featured && "lg:p-8";
  let t5;
  if ($[0] !== className || $[1] !== t3 || $[2] !== t4) {
    t5 = cn("group border-border/50 bg-card relative overflow-hidden border p-6 motion-safe:transition-all motion-safe:duration-300", PUBLIC_PANEL_RADIUS, t3, t4, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = comingSoon && "opacity-70";
  let t7;
  if ($[4] !== t6) {
    t7 = cn("from-brand-primary/10 to-brand-accent/10 text-brand-accent dark:from-brand-primary/20 dark:to-brand-accent/20 inline-flex rounded-xl bg-linear-to-br p-3", t6);
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== icon || $[7] !== t7) {
    t8 = <div className={t7} aria-hidden="true">{icon}</div>;
    $[6] = icon;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== comingSoon) {
    t9 = comingSoon ? <Link className="bg-muted text-muted-foreground hover:text-foreground rounded-full px-3 py-1 text-xs font-medium transition-colors" href="/planos">Em breve: ver planos</Link> : null;
    $[9] = comingSoon;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== category) {
    t10 = <span className="bg-brand-accent/10 text-brand-accent rounded-full px-3 py-1 text-sm font-medium">{category}</span>;
    $[11] = category;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t9) {
    t11 = <div className="flex flex-col items-end gap-1.5">{t9}{t10}</div>;
    $[13] = t10;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t8) {
    t12 = <div className="mb-4 flex items-start justify-between gap-4">{t8}{t11}</div>;
    $[16] = t11;
    $[17] = t8;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = featured ? "text-xl" : "text-lg";
  let t14;
  if ($[19] !== t13) {
    t14 = cn("mb-2 font-semibold", t13);
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t14 || $[22] !== title) {
    t15 = <dt className={t14}>{title}</dt>;
    $[21] = t14;
    $[22] = title;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== description) {
    t16 = <dd className="text-muted-foreground text-base/relaxed">{description}</dd>;
    $[24] = description;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t12 || $[27] !== t15 || $[28] !== t16 || $[29] !== t5) {
    t17 = <dl className={t5}>{t12}{t15}{t16}</dl>;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
    $[29] = t5;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
}